// @ts-check
// =============================================================================
// js/app/analytics.js — gameplay stats pings (port of CallStatsAPI :7277).
//
// WHAT THIS IS
//   Fire-and-forget event strings to the external stats relay, WIRE-FAITHFUL
//   to the old client (OldClientReference/index.js :7196–7338) — the relay
//   only understands that shape, so every detail below mirrors it:
//     - payload is {stats: ["Deckbiter:<category>:<key>", ...]} — the game
//       name is baked into every string; there is NO separate game field,
//     - a plain key maps to the "core" category ("Init" ->
//       "Deckbiter:core:Init"); "Category:Key" keeps its category,
//     - whenever core:Init is stale (20h dedup), ANY ping also emits the
//       visit breakdowns: New/Returning (from the ledger itself),
//       Mobile/Desktop, Android/IOS (mobile only), and the tod<0|4|...|20>
//       UTC time-of-day bucket,
//     - session-time milestones are core keys time15..time180, tracked in
//       sessionStorage with a 3-hour session reset,
//     - sends retry twice with backoff and check response.ok; a dead relay
//       disables the pipeline for the rest of the page load (analytics must
//       never affect gameplay).
//
// WHO CALLS IT
//   session.js ("Init", subscription tier, Source channel), lobby
//   (Duel:Host/Join), challenge ladder (Duel:Bot), quickplay
//   (Duel:Quickplay), deck builder (Deckbuilding:*), boot (initAnalytics for
//   the time milestones).
// =============================================================================

import { shellPortal } from './shell-mode.js';
import { isQuickplay } from './quickplay-mode.js';

const STATS_GAME = 'Deckbiter';
const STATS_ENDPOINT = 'https://fffme.replit.app/LogStats';
const STATS_STORAGE_KEY = 'statsTracker_Deckbiter';
const STATS_SESSION_KEY = 'statsSession_Deckbiter';
const STATS_DEDUP_HOURS = 20;
/** Session-length milestones (minutes), reported as core time<m> (old :7204). */
const STATS_TIME_MILESTONES = [15, 30, 45, 60, 75, 90, 105, 120, 135, 150, 165, 180];
/** A session left idle this long restarts its milestone clock (old :7247). */
const STATS_SESSION_RESET_MS = 3 * 60 * 60 * 1000;

/**
 * Where did this visit come from? Reported as "Source:<name>" alongside Init
 * (session.js), so the dashboard splits channels:
 *   - a ?portal=<name> shell wins (shell-mode.js sanitizes + remembers it):
 *     portals/itch -> Source:Itch, portals/newgrounds -> Source:Newgrounds,
 *   - an unbadged iframe   -> Embedded,
 *   - the quickplay link   -> Quickplay,
 *   - everything else      -> Direct.
 * @returns {string}
 */
export function trafficSource() {
	const portal = shellPortal();
	if (portal === 'embedded') return 'Embedded';
	if (portal) return portal[0].toUpperCase() + portal.slice(1);
	if (isQuickplay()) return 'Quickplay';
	return 'Direct';
}

let statsDead = false;

/**
 * POST fully-qualified stat strings to the relay (old _sendStats :7208):
 * two retries with linear backoff, and a non-ok response counts as a failure
 * too. After the retries are spent the pipeline turns off for this load.
 * @param {string[]} statsArray
 * @param {number} [retries]
 * @returns {void}
 */
function sendStats(statsArray, retries = 0) {
	if (statsDead || !statsArray.length) return;
	fetch(STATS_ENDPOINT, {
		method: 'POST',
		headers: { 'Content-Type': 'application/json' },
		body: JSON.stringify({ stats: statsArray }),
	}).then((response) => {
		if (response.ok) return;
		if (retries < 2) {
			setTimeout(() => sendStats(statsArray, retries + 1), 2000 * (retries + 1));
		} else {
			statsDead = true;
		}
	}).catch(() => {
		if (retries < 2) {
			setTimeout(() => sendStats(statsArray, retries + 1), 2000 * (retries + 1));
		} else {
			statsDead = true;
		}
	});
}

/**
 * Read the dedup ledger: { "Deckbiter:<cat>:<key>": lastSentMs }.
 * @returns {Record<string, number>}
 */
function readLedger() {
	try {
		return JSON.parse(localStorage.getItem(STATS_STORAGE_KEY) || '{}');
	} catch {
		return {};
	}
}

/**
 * Report an event once per dedup window (old CallStatsAPI :7281).
 * Whenever core:Init is stale, ANY call also emits the visit breakdowns —
 * exactly like the old client, so an "Init" ping and, say, a "Duel:Bot"
 * ping at the top of a fresh visit produce identical dashboards.
 * @param {string} statString  e.g. "Init", "Duel:Host", "Subscription:None"
 * @returns {void}
 */
export function callStatsAPI(statString) {
	try {
		const ledger = readLedger();
		const now = Date.now();
		const windowMs = STATS_DEDUP_HOURS * 60 * 60 * 1000;
		/** @type {string[]} */
		const toSend = [];

		// --- The implicit visit block (old :7292–7310) ------------------------
		const initKey = STATS_GAME + ':core:Init';
		// New vs Returning comes from the ledger itself: has this browser ever
		// sent an Init before? (The 18+ flag can't stand in — the gate stores
		// it seconds before the first Init.)
		const isReturning = !!ledger[initKey];
		if (!ledger[initKey] || now - ledger[initKey] > windowMs) {
			toSend.push(initKey);
			ledger[initKey] = now;
			toSend.push(STATS_GAME + ':core:' + (isReturning ? 'Returning' : 'New'));
			const ua = navigator.userAgent || '';
			const isMobile = /Mobi|Android|iPhone|iPad|iPod/i.test(ua);
			toSend.push(STATS_GAME + ':core:' + (isMobile ? 'Mobile' : 'Desktop'));
			if (isMobile) {
				if (/Android/i.test(ua)) toSend.push(STATS_GAME + ':core:Android');
				else if (/iPhone|iPad|iPod/i.test(ua)) toSend.push(STATS_GAME + ':core:IOS');
			}
			const utcHour = new Date().getUTCHours();
			toSend.push(STATS_GAME + ':core:tod' + Math.floor(utcHour / 4) * 4);
		}

		// --- The requested stat, fully qualified (old :7316–7333) -------------
		const colonIdx = statString.indexOf(':');
		const category = colonIdx > 0 ? statString.slice(0, colonIdx).trim() : 'core';
		const key = colonIdx > 0 ? statString.slice(colonIdx + 1).trim() : statString.trim();
		if (key) {
			const fullKey = STATS_GAME + ':' + category + ':' + key;
			if (!ledger[fullKey] || now - ledger[fullKey] > windowMs) {
				toSend.push(fullKey);
				ledger[fullKey] = now;
			}
		}

		if (!toSend.length) return;
		localStorage.setItem(STATS_STORAGE_KEY, JSON.stringify(ledger));
		sendStats(toSend);
	} catch {
		// Analytics must never break the game.
	}
}

/**
 * Session-time milestones (old _initTimeTracking/_checkTimeMilestones :7242):
 * core time15..time180, dedup'd per session via sessionStorage; a session
 * idle past 3 hours starts a fresh clock. Called once at boot.
 * @returns {void}
 */
export function initAnalytics() {
	try {
		/** @type {{startTime: number, sent: Record<string, boolean>}|null} */
		let session = null;
		try {
			session = JSON.parse(sessionStorage.getItem(STATS_SESSION_KEY) || 'null');
		} catch {
			session = null;
		}
		const now = Date.now();
		if (!session || !session.startTime || now - session.startTime > STATS_SESSION_RESET_MS) {
			session = { startTime: now, sent: {} };
			sessionStorage.setItem(STATS_SESSION_KEY, JSON.stringify(session));
		}

		const check = () => {
			const elapsed = Math.floor((Date.now() - session.startTime) / 60000);
			/** @type {string[]} */
			const toSend = [];
			for (const milestone of STATS_TIME_MILESTONES) {
				if (elapsed >= milestone && !session.sent['time' + milestone]) {
					session.sent['time' + milestone] = true;
					toSend.push(STATS_GAME + ':core:time' + milestone);
				}
			}
			if (toSend.length) {
				sessionStorage.setItem(STATS_SESSION_KEY, JSON.stringify(session));
				sendStats(toSend);
			}
			const next = STATS_TIME_MILESTONES.find((m) => !session.sent['time' + m]);
			if (next) {
				const elapsedNow = Math.floor((Date.now() - session.startTime) / 60000);
				setTimeout(check, Math.max((next - elapsedNow) * 60000, 30_000));
			}
		};
		check();
	} catch {
		// sessionStorage unavailable — skip milestones.
	}
}
